/**
 * captureSession.js  —  Live config capture engine
 *
 * Accumulates app-specific config from live MITM traffic while the tester
 * scrolls through the app on their device.
 *
 * Captured per request type:
 *   GAM (google):   IU path, banner sizes per IU, correlator (for Prebid linking)
 *   Prebid:         stored request ID, publisher ID, bundle ID, bidder names,
 *                   banner sizes from imp.banner.format
 *   APS (amazon):   slot UUID, sizes, appId
 *
 * Prebid↔GAM correlation (temporal):
 *   Prebid Mobile SDK fires one OpenRTB request per slot, then GAM fires for the
 *   same slot 100–600 ms later.  We maintain a rolling queue of unmatched Prebid
 *   entries and pop the oldest one when a GAM request arrives with a matching
 *   platform code.  Match confidence is high for single-slot pages; for multi-slot
 *   pages we fall back to index ordering.
 *
 * API:
 *   captureSession.start(appId, profileId)
 *   captureSession.addEntry(rawEntry)
 *   captureSession.getSummary()           → plain-object snapshot
 *   captureSession.stop()
 *   captureSession.compareToBaseline(bl)  → change list
 *   captureSession.isActive               → bool
 */

/* ─── Baseline helpers ───────────────────────────────────────────────────── */

const BASELINE_PREFIX = 'adlensfx_baseline_';
const baselineKey = (a, p) => `${BASELINE_PREFIX}${a}_${p}`;

export function loadBaseline(appId, profileId) {
  try { return JSON.parse(localStorage.getItem(baselineKey(appId, profileId)) || 'null'); }
  catch { return null; }
}
export function saveBaseline(appId, profileId, data) {
  try { localStorage.setItem(baselineKey(appId, profileId), JSON.stringify(data)); } catch {}
}

/* ─── URL / body parsers ─────────────────────────────────────────────────── */

function parseQS(url = '') {
  const out = {};
  try {
    const idx = url.indexOf('?');
    if (idx < 0) return out;
    url.slice(idx + 1).split('&').forEach(pair => {
      const eq = pair.indexOf('=');
      if (eq < 0) return;
      const k = decodeURIComponent(pair.slice(0, eq).replace(/\+/g, ' '));
      const v = decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' '));
      out[k] = v;
    });
  } catch {}
  return out;
}

function tryParseBody(entry) {
  const raw = entry?.requestBodyParsed || entry?.requestBody || entry?.validData?.raw;
  if (!raw) return null;
  try { return typeof raw === 'object' ? raw : JSON.parse(raw); } catch { return null; }
}

/**
 * Derive a mapping key from an IU path.
 * /4145/fnc/iosmob/hp/ban1  →  fnc_iosmob_hp_banner
 * /4145/fnc/iosmob/art/pol/adh  →  fnc_iosmob_art_adh
 */
function iuToKey(iu = '') {
  const parts = iu.replace(/^\//, '').split('/').filter(Boolean);
  if (parts.length < 5) return null;
  const brand    = parts[1];
  const platform = parts[2];
  const page     = parts[3];
  const pos      = parts[parts.length - 1];
  const normPos  = /^ban\d+$/i.test(pos) ? 'banner' : pos;
  return `${brand}_${platform}_${page}_${normPos}`.toLowerCase();
}

/**
 * Pull banner sizes from an APS slot object. APS carries size either as a `sz`
 * string ("300x250" or comma/pipe-joined "300x250,320x50") or, less commonly, a
 * `sizes` array ([[w,h]] or ["300x250"]). Returns a clean ['WxH', …] array.
 */
function _extractSlotSizes(slot = {}) {
  const out = [];
  const pushIf = (t) => { const s = String(t).trim(); if (/^\d+x\d+$/.test(s)) out.push(s); };
  if (slot.sz) String(slot.sz).split(/[,|]/).forEach(pushIf);
  if (Array.isArray(slot.sizes)) {
    slot.sizes.forEach(sz => pushIf(Array.isArray(sz) ? `${sz[0]}x${sz[1]}` : sz));
  }
  return out;
}

/** Extract platform segment from IU: /4145/fnc/iosmob/... → 'iosmob' */
function platformFromIU(iu = '') {
  const parts = iu.replace(/^\//, '').split('/').filter(Boolean);
  return parts[2] || null;
}

/* ─── Module-level singleton ─────────────────────────────────────────────── */

const _s = {
  active:       false,
  appId:        null,
  profileId:    null,
  appName:      null,   // display name, e.g. 'Example App'
  profileName:  null,   // display name, e.g. 'iOS Mobile'
  startedAt:    null,
  reqCount:     0,

  // Core captured data
  iuSizes:      {},   // { iu: Set<'WxH'> }     — sizes seen per IU
  prebidMap:    {},   // { key: storedRequestId } — e.g. fnc_iosmob_hp_banner → '18933712'
  prebidAllIds: new Set(),  // all storedIds seen (even if IU couldn't be correlated)
  amazonSlots:  {},   // { uuid: { sizes: Set, label: string } }
  publisherIds: new Set(),
  bundleIds:    new Set(),
  bidders:      new Set(),

  // Temporal correlation queue (Prebid → GAM)
  // Each entry: { storedId, platform, impSizes, ts }
  _prebidQ:     [],
};

/* ─── Correlation helper ─────────────────────────────────────────────────── */

const CORR_WINDOW_MS = 2500;  // Prebid and GAM must be within this window

function _popMatchingPrebid(platform, now = Date.now()) {
  // Remove stale entries
  const fresh = _s._prebidQ.filter(e => now - e.ts < CORR_WINDOW_MS);
  _s._prebidQ = fresh;
  if (fresh.length === 0) return null;

  // Find the oldest entry that matches the platform (or any if platform unknown)
  const idx = fresh.findIndex(e => !e.platform || !platform || e.platform === platform);
  if (idx < 0) return null;

  const matched = fresh[idx];
  _s._prebidQ.splice(idx, 1);
  return matched;
}

/* ─── Public API ─────────────────────────────────────────────────────────── */

export const captureSession = {

  get isActive()    { return _s.active; },
  get appId()       { return _s.appId; },
  get profileId()   { return _s.profileId; },
  get appName()     { return _s.appName; },
  get profileName() { return _s.profileName; },

  start(appId, profileId, { appName = null, profileName = null } = {}) {
    _s.active       = true;
    _s.appId        = appId;
    _s.profileId    = profileId;
    _s.appName      = appName;
    _s.profileName  = profileName;
    _s.startedAt    = Date.now();
    _s.reqCount     = 0;
    _s.iuSizes      = {};
    _s.prebidMap    = {};
    _s.prebidAllIds = new Set();
    _s.amazonSlots  = {};
    _s.publisherIds = new Set();
    _s.bundleIds    = new Set();
    _s.bidders      = new Set();
    _s._prebidQ     = [];
  },

  stop() {
    _s.active = false;
  },

  /** Feed one raw event entry to the capture engine. Safe to call when inactive. */
  addEntry(entry) {
    if (!entry) return;
    _s.reqCount++;
    const { requestType, url = '', validData } = entry;
    const now = Date.now();

    /* ── GAM auction request ──────────────────────────────────────────── */
    if (requestType === 'google') {
      const qs  = parseQS(url);
      const iu  = qs.iu ? decodeURIComponent(qs.iu) : null;
      if (!iu) return;

      // Track IU + sizes
      if (!_s.iuSizes[iu]) _s.iuSizes[iu] = new Set();
      const szRaw = qs.sz || '';
      szRaw.split('|').forEach(s => { if (s) _s.iuSizes[iu].add(s); });

      // Temporal correlation: pop matching Prebid entry and create mapping
      const key      = iuToKey(iu);
      const platform = platformFromIU(iu);
      if (key) {
        const matched = _popMatchingPrebid(platform, now);
        if (matched && !_s.prebidMap[key]) {
          _s.prebidMap[key] = matched.storedId;
          // Backfill sizes from Prebid imp.banner.format if GAM sz= was empty
          if (_s.iuSizes[iu].size === 0 && matched.impSizes?.length) {
            matched.impSizes.forEach(sz => _s.iuSizes[iu].add(sz));
          }
        }
      }
    }

    /* ── Prebid OpenRTB request ───────────────────────────────────────── */
    if (requestType === 'prebid') {
      const body = tryParseBody(entry);
      if (!body) return;

      // Publisher ID + Bundle ID
      const pubId  = body?.app?.publisher?.id;
      const bundle = body?.app?.bundle;
      if (pubId)  _s.publisherIds.add(pubId);
      if (bundle) _s.bundleIds.add(bundle);

      // Bidder names from imp[0].ext.prebid.bidder
      const bidderMap = body?.imp?.[0]?.ext?.prebid?.bidder || {};
      Object.keys(bidderMap).forEach(b => _s.bidders.add(b));

      // Each imp → stored request ID, linked via GPID
      // GPID (Global Placement ID) is the most reliable link: it contains the
      // full GAM ad unit path, e.g. "/4145/fnc/iosmob/hp/ban1", so we can
      // derive the mapping key directly without timing-based correlation.
      // Fields checked in priority order:
      //   imp.ext.gpid              — Prebid Mobile SDK 2.x standard
      //   imp.ext.data.pbadslot     — older Prebid Mobile
      //   imp.ext.data.adUnitCode   — some custom integrations
      const imps = Array.isArray(body?.imp) ? body.imp : [];
      imps.forEach(imp => {
        const storedId = imp?.ext?.prebid?.storedrequest?.id;
        if (!storedId) return;
        _s.prebidAllIds.add(storedId);

        // ── GPID-based direct linking (preferred) ──────────────────────
        const gpid =
          imp?.ext?.gpid ||
          imp?.ext?.data?.pbadslot ||
          imp?.ext?.data?.adUnitCode ||
          null;

        if (gpid) {
          const key = iuToKey(gpid);
          if (key && !_s.prebidMap[key]) {
            _s.prebidMap[key] = storedId;
            // Also track the IU + sizes from the GPID if not yet captured from GAM
            if (!_s.iuSizes[gpid]) _s.iuSizes[gpid] = new Set();
            (imp?.banner?.format || [])
              .map(f => `${f.w}x${f.h}`)
              .filter(Boolean)
              .forEach(sz => _s.iuSizes[gpid].add(sz));
          }
        } else {
          // ── Fallback: temporal correlation queue (for older SDKs without GPID) ──
          const impSizes = (imp?.banner?.format || []).map(f => `${f.w}x${f.h}`).filter(Boolean);
          _s._prebidQ.push({ storedId, platform: null, impSizes, ts: now });
        }
      });
    }

    /* ── Amazon APS (display) request ────────────────────────────────── */
    if (requestType === 'amazon') {
      const body = tryParseBody(entry);
      const vd   = validData || {};

      // App ID (bundle)
      const appId = body?.appId || vd?.appId;
      if (appId) _s.bundleIds.add(appId);

      // An APS request carries MULTIPLE slots, each with its own UUID + size.
      // Earlier this only read slots[0] and looked for a `sizes` array, but the
      // APS DTB body carries size in a `sz` string field (e.g. "300x250" or
      // "300x250,320x50"), which is how AdLifecycleView reads it (slot.sz).
      const slotList =
        (Array.isArray(body?.slots) && body.slots) ||
        (Array.isArray(vd?.slots) && vd.slots) ||
        (Array.isArray(vd?.expanded?.slots) && vd.expanded.slots) ||
        [];

      slotList.forEach(slot => {
        const uuid = slot?.slot || slot?.slotID || slot?.slotId || slot?.id;
        if (!uuid) return;
        const sizes = _extractSlotSizes(slot);
        if (!_s.amazonSlots[uuid]) {
          const slotNum = Object.keys(_s.amazonSlots).length + 1;
          _s.amazonSlots[uuid] = { sizes: new Set(sizes), label: slot?.slotName || `Slot ${slotNum}` };
        } else {
          sizes.forEach(s => _s.amazonSlots[uuid].sizes.add(s));
        }
      });
    }
  },

  /** Returns a serialisable snapshot of everything captured so far. */
  getSummary() {
    // Flatten iuSizes
    const iuRows = Object.entries(_s.iuSizes).map(([iu, szSet]) => ({
      iu,
      sizes: [...szSet].sort(),
      key:   iuToKey(iu),
      platform: platformFromIU(iu),
      prebidId: iuToKey(iu) ? _s.prebidMap[iuToKey(iu)] || null : null,
    })).sort((a, b) => a.iu.localeCompare(b.iu));

    // All sizes (union)
    const allSizes = [...new Set(iuRows.flatMap(r => r.sizes))].sort();

    // Amazon slots as plain object
    const amazonSlots = Object.fromEntries(
      Object.entries(_s.amazonSlots).map(([uuid, d]) => [
        uuid,
        { sizes: [...d.sizes].sort(), label: d.label },
      ])
    );

    // Prebid IDs not yet linked to a GAM IU
    const linkedIds   = new Set(Object.values(_s.prebidMap));
    const unmatchedIds = [..._s.prebidAllIds].filter(id => !linkedIds.has(id));

    return {
      appId:        _s.appId,
      profileId:    _s.profileId,
      capturedAt:   _s.startedAt,
      reqCount:     _s.reqCount,

      iuRows,                        // per-IU detail with sizes + prebidId
      allSizes,                      // union of all banner sizes
      prebidMappings: { ..._s.prebidMap },   // key → storedId (linked)
      prebidAllIds:   [..._s.prebidAllIds],  // all storedIds seen
      unmatchedIds,                  // storedIds we couldn't link to an IU

      amazonSlots,
      publisherIds: [..._s.publisherIds],
      bundleIds:    [..._s.bundleIds],
      bidders:      [..._s.bidders].sort(),
    };
  },

  /** Diff current capture against a saved baseline → array of change descriptors. */
  compareToBaseline(baseline) {
    if (!baseline) return [];
    const s = this.getSummary();
    const changes = [];

    // New IUs
    const knownIUs = new Set(baseline.iuRows?.map(r => r.iu) || baseline.adUnits || []);
    s.iuRows.forEach(r => {
      if (!knownIUs.has(r.iu))
        changes.push({ type: 'new_iu', iu: r.iu, sizes: r.sizes, label: `New ad unit: ${r.iu}` });
    });

    // New / changed Prebid IDs
    const knownMap = baseline.prebidMappings || {};
    Object.entries(s.prebidMappings).forEach(([key, id]) => {
      if (!knownMap[key])
        changes.push({ type: 'new_prebid', key, id, label: `New Prebid mapping: ${key} → ${id}` });
      else if (knownMap[key] !== id)
        changes.push({ type: 'changed_prebid', key, oldId: knownMap[key], newId: id, label: `Prebid ID changed: ${key}` });
    });

    // New Amazon slots
    const knownSlots = new Set(Object.keys(baseline.amazonSlots || {}));
    Object.keys(s.amazonSlots).forEach(uuid => {
      if (!knownSlots.has(uuid))
        changes.push({ type: 'new_slot', uuid, label: `New Amazon slot: ${uuid.slice(0, 16)}…` });
    });

    // New sizes
    const knownSizes = new Set(baseline.allSizes || []);
    s.allSizes.forEach(sz => {
      if (!knownSizes.has(sz))
        changes.push({ type: 'new_size', size: sz, label: `New banner size: ${sz}` });
    });

    return changes;
  },
};
